import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { useCallback, useEffect, useRef, useState } from 'react';

// TODO: 채널톡 콘솔에서 발급받은 실제 pluginKey 로 교체할 것
const CHANNEL_TALK_PLUGIN_KEY = 'a14e290f-71d1-4fb7-8600-2f575fbfb64a';

/** 상담 창에서 열 채널톡 워크플로우 ID (화면별로 다르게 사용) */
export const CHANNEL_TALK_WORKFLOW_ID = {
  /** 데스크톱 대시보드(웹) */
  WEB: '842281',
  /** 카톡 진입(모바일) */
  STIMHOME: '825045',
} as const;

interface ChannelTalkProfile {
  /** 채널톡 대화방을 분리하는 식별자(직원 id). 이 값이 없으면 부팅하지 않는다. */
  memberId?: number | string;
  /** 프로필 이름 */
  name?: string;
  /** 프로필 전화번호 */
  tel?: string;
  /** 병원 계정 여부. true 면 name 앞에 [병원] 을 붙인다. */
  isHospital?: boolean;
  /** 채널톡 고객 태그. (참조 안정성을 위해 호출부에서 메모이즈할 것) */
  tags?: string[];
  /** openWorkflow 로 열 워크플로우 ID */
  workflowId?: string;
  /** 상담창(메신저)이 닫힐 때 호출된다. */
  onCloseMessenger?: () => void;
}

/**
 * 한국 전화번호를 채널톡 필드(E.164)로 매핑한다.
 * 양식에 맞지 않으면 둘 다 null 을 반환해 프로필에 설정하지 않는다.
 * (010 + 8자리 → mobileNumber, 유효 지역/인터넷 번호 → landlineNumber)
 */
function toChannelPhone(tel?: string): { mobileNumber: string | null; landlineNumber: string | null } {
  const empty = { mobileNumber: null, landlineNumber: null };
  const digits = (tel ?? '').replace(/\D/g, '');
  if (!digits) return empty;
  // 국제표기(82/+82)를 국내 0-prefix 로 정규화
  const local = digits.startsWith('82') ? `0${digits.slice(2)}` : digits;
  const isMobile = /^010\d{8}$/.test(local);
  const isLandline = /^0[2-7]\d{7,9}$/.test(local); // 지역번호(02·0XX)·070 등 9~11자리
  if (!isMobile && !isLandline) return empty;
  const e164 = `+82${local.slice(1)}`;
  return isMobile
    ? { mobileNumber: e164, landlineNumber: null }
    : { mobileNumber: null, landlineNumber: e164 };
}

/**
 * 로그인 이후 화면에서 채널톡을 부팅하는 훅. 기본 런처는 숨기고(커스텀 버튼 사용),
 * 반환한 openWorkflow 로 지정 워크플로우 상담 창을 연다.
 * 직원 id(memberId) 로 부팅해 계정별로 대화방을 분리하고,
 * 병원 정보가 확인되면 채널톡 프로필의 기본 이름(name)/전화번호(mobileNumber·landlineNumber)에 반영한다.
 * memberId 가 바뀌거나 언마운트(로그아웃 등)되면 shutdown 으로 세션을 정리한다.
 */
export function useChannelTalk({
  memberId,
  name,
  tel,
  isHospital,
  tags,
  workflowId,
  onCloseMessenger,
}: ChannelTalkProfile = {}): {
  openWorkflow: () => void;
  isBooted: boolean;
} {
  // boot 콜백이 실제로 완료된 뒤 true. 이 값으로 런처 버튼 노출 시점을 맞춘다.
  const [isBooted, setIsBooted] = useState(false);

  // 콜백은 boot 완료 후 등록하므로, 항상 최신 핸들러를 참조하도록 ref 로 보관한다.
  const onCloseRef = useRef(onCloseMessenger);
  useEffect(() => {
    onCloseRef.current = onCloseMessenger;
  }, [onCloseMessenger]);

  useEffect(() => {
    if (memberId == null) return;
    // 비동기 boot 콜백이 정리 이후 실행되는 경우를 막는 플래그.
    let active = true;
    ChannelService.loadScript();
    ChannelService.boot(
      {
        pluginKey: CHANNEL_TALK_PLUGIN_KEY,
        memberId: String(memberId),
        hideChannelButtonOnBoot: true,
      },
      (error) => {
        if (!active || error) return;
        // boot 완료 후 이벤트 핸들러를 등록해야 유실되지 않는다.
        ChannelService.onHideMessenger(() => onCloseRef.current?.());
        setIsBooted(true);
      }
    );

    return () => {
      active = false;
      setIsBooted(false);
      ChannelService.clearCallbacks();
      ChannelService.shutdown();
    };
  }, [memberId]);

  useEffect(() => {
    // 부팅(memberId) 이후에만 프로필/태그를 갱신한다.
    if (memberId == null) return;
    const hasTags = !!tags?.length;
    if (!name && !tel && !hasTags) return;
    const { mobileNumber, landlineNumber } = toChannelPhone(tel);
    ChannelService.updateUser({
      profile: {
        name: name ? (isHospital ? `[병원]${name}` : name) : null,
        mobileNumber,
        landlineNumber,
      },
      ...(hasTags ? { tags } : {}),
    });
  }, [memberId, name, tel, isHospital, tags]);

  const openWorkflow = useCallback(() => {
    if (!workflowId) return;
    ChannelService.openWorkflow(workflowId);
  }, [workflowId]);

  return { openWorkflow, isBooted };
}
